import type { PointType } from '@/api/services/layer.service'
import type { MetadataNode } from '@/api/services/project.service'

export type NavigationKeypointConfig = {
  name: string
  field: string
}

const mergePluginFields = (
  pluginData: PointType['plugin_data'] | null | undefined,
): Record<string, unknown> => {
  if (!pluginData) return {}

  return Object.values(pluginData).reduce<Record<string, unknown>>((acc, value) => {
    if (value && typeof value === 'object') {
      Object.assign(acc, value as Record<string, unknown>)
    }
    return acc
  }, {})
}

export const getNavigationFieldId = (
  metadataNodes: MetadataNode[],
  typeName: string,
  fieldName: string,
): number | null => {
  const nodeType = metadataNodes.find((node) => node.name === typeName)
  if (!nodeType?.plugin_data) return null

  const plugins = Object.values(nodeType.plugin_data as Record<string, unknown>)

  for (const plugin of plugins) {
    const fields = (plugin as { fields?: Array<{ id: number; name: string }> })?.fields
    const field = fields?.find((item) => item.name === fieldName)
    if (field?.id) return field.id
  }

  return null
}

const parseMarkerIds = (value: unknown): number[] => {
  if (value == null || value === '') return []

  return String(value)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map(Number)
    .filter((id) => Number.isFinite(id) && id > 0)
}

export const buildKeypointGraph = (
  keypoints: PointType[],
  fieldId: number,
): Map<number, number[]> => {
  const fieldKey = `field_${fieldId}`
  const graph = new Map<number, number[]>()

  for (const point of keypoints) {
    const merged = mergePluginFields(point.plugin_data)
    graph.set(point.id, parseMarkerIds(merged[fieldKey]))
  }

  return graph
}

export type MockKeypoint = {
  id: number
  x: number
  y: number
  neighbors: number[]
}

export const findNearestNodeId = (
  nodes: Array<{ id: number; x: number; y: number }>,
  x: number,
  y: number,
): number | null => {
  if (!nodes.length) return null

  let nearest = nodes[0]
  let minDistance = Number.POSITIVE_INFINITY

  for (const node of nodes) {
    const distance = (node.x - x) ** 2 + (node.y - y) ** 2
    if (distance < minDistance) {
      nearest = node
      minDistance = distance
    }
  }

  return nearest.id
}

export const findNearestKeypointId = (
  keypoints: PointType[],
  x: number,
  y: number,
): number | null => findNearestNodeId(keypoints, x, y)

export const findRoutePath = (
  graph: Map<number, number[]>,
  startId: number,
  endId: number,
): number[] | null => {
  if (startId === endId) return [startId]

  const queue = [startId]
  const visited = new Set<number>([startId])
  const parent = new Map<number, number>()

  while (queue.length) {
    const current = queue.shift()
    if (current == null) break

    if (current === endId) {
      const path = [endId]
      let node = endId

      while (node !== startId) {
        const previous = parent.get(node)
        if (previous == null) break
        node = previous
        path.unshift(node)
      }

      return path
    }

    for (const neighborId of graph.get(current) ?? []) {
      if (visited.has(neighborId)) continue

      visited.add(neighborId)
      parent.set(neighborId, current)
      queue.push(neighborId)
    }
  }

  return null
}

const dedupeCoordinates = (coords: [number, number][]): [number, number][] =>
  coords.filter((coord, index) => {
    if (index === 0) return true

    const prev = coords[index - 1]
    return prev[0] !== coord[0] || prev[1] !== coord[1]
  })

export const buildRouteFromKeypoints = ({
  from,
  to,
  layerPoints,
  keypointConfigs,
  metadataNodes,
}: {
  from: { x: number; y: number }
  to: { x: number; y: number }
  layerPoints: PointType[]
  keypointConfigs: NavigationKeypointConfig[]
  metadataNodes: MetadataNode[]
}): [number, number][] | null => {
  if (!keypointConfigs.length || !layerPoints.length) return null

  const keypointTypeNames = keypointConfigs.map((config) => config.name)
  const keypoints = layerPoints.filter((point) =>
    keypointTypeNames.includes(point.type_name),
  )

  if (keypoints.length < 2) return null

  const fieldIds = keypointConfigs
    .map((config) => getNavigationFieldId(metadataNodes, config.name, config.field))
    .filter((fieldId): fieldId is number => fieldId != null)

  if (!fieldIds.length) return null

  const graph = buildKeypointGraph(keypoints, fieldIds[0])
  const startId = findNearestKeypointId(keypoints, from.x, from.y)
  const endId = findNearestKeypointId(keypoints, to.x, to.y)

  if (startId == null || endId == null) return null

  const pathIds = findRoutePath(graph, startId, endId)
  if (!pathIds?.length) return null

  const pointById = new Map(keypoints.map((point) => [point.id, point]))
  const coordinates: [number, number][] = [[from.x, from.y]]

  for (const pointId of pathIds) {
    const point = pointById.get(pointId)
    if (point) {
      coordinates.push([point.x, point.y])
    }
  }

  coordinates.push([to.x, to.y])

  return dedupeCoordinates(coordinates)
}

export const buildMockKeypointGraph = (
  keypoints: MockKeypoint[],
): Map<number, number[]> => {
  const graph = new Map<number, number[]>()

  for (const keypoint of keypoints) {
    graph.set(keypoint.id, keypoint.neighbors ?? [])
  }

  return graph
}

export const buildRouteFromMockKeypoints = ({
  from,
  to,
  mockKeypoints,
}: {
  from: { x: number; y: number }
  to: { x: number; y: number }
  mockKeypoints: MockKeypoint[]
}): [number, number][] | null => {
  if (mockKeypoints.length < 2) return null

  const graph = buildMockKeypointGraph(mockKeypoints)
  const startId = findNearestNodeId(mockKeypoints, from.x, from.y)
  const endId = findNearestNodeId(mockKeypoints, to.x, to.y)

  if (startId == null || endId == null) return null

  const pathIds = findRoutePath(graph, startId, endId)
  if (!pathIds?.length) return null

  const pointById = new Map(mockKeypoints.map((keypoint) => [keypoint.id, keypoint]))
  const coordinates: [number, number][] = [[from.x, from.y]]

  for (const pointId of pathIds) {
    const keypoint = pointById.get(pointId)
    if (keypoint) {
      coordinates.push([keypoint.x, keypoint.y])
    }
  }

  coordinates.push([to.x, to.y])

  return dedupeCoordinates(coordinates)
}
